import { AspectRatio, Button, Image, Select, Skeleton } from '@mantine/core'
import classes from './MatchSummary.module.css'
import clsx from 'clsx'
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import { Match, useCompetitionRounds, useMatches } from '../../matches/api'
import {
  Metric,
  useMetrics,
  DataPoint,
  useMetricsData,
} from '../../widgets/api'
import { FmtColored } from '../../widgets/format'
import { Suspense, useMemo, useState } from 'react'
import { Typography } from '../../common/typography'
import { Link, useLocation } from 'react-router'

type MatchStats = {
  [kpi_key: string]: {
    [round: string]: {
      value: number
    }
  }
}

function SummaryColumn({
  match,
  kpis,
  teamId,
  component,
  data,
  competitionId,
}: {
  match: Match
  kpis: Metric[]
  teamId: number
  component: string
  data: MatchStats
  competitionId: number
}) {
  const location = useLocation()
  const isHomeTeam = match.home_team_id === teamId
  const logo = isHomeTeam ? match.away_team_logo : match.home_team_logo
  return (
    <div className={classes.summary_column_container}>
      <Typography fc={{ light: 'black', dark: 'gray-3' }} fw={500} lh="125">
        {`R${match.round_number}`}
      </Typography>
      <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
        {new Date(match.round_start).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })}
      </Typography>
      <Typography
        fc={{ light: 'white', dark: 'gray-3' }}
        ta="center"
        ff="JetBrains Mono"
        lh="150"
        className={clsx(classes.result, {
          [classes.green]:
            (teamId === match.home_team_id &&
              match.home_team_score > match.away_team_score) ||
            (teamId === match.away_team_id &&
              match.home_team_score < match.away_team_score),
          [classes.red]:
            (teamId === match.home_team_id &&
              match.home_team_score < match.away_team_score) ||
            (teamId === match.away_team_id &&
              match.home_team_score > match.away_team_score),
          [classes.gray]: match.home_team_score === match.away_team_score,
        })}
      >
        {match.home_team_score}:{match.away_team_score}
      </Typography>
      <AspectRatio ratio={1 / 1}>
        <Image
          src={logo.thumbnail}
          className={classes.opponent_team_logo}
          fit="scale-down"
        />
      </AspectRatio>
      {kpis.map((kpi) => (
        <Typography
          key={kpi.key}
          fw={500}
          lh="140"
          ff="JetBrains Mono"
          ta="center"
        >
          <FmtColored
            value={data[kpi.key]?.[match.round_number]?.value}
            metric={kpi.metric_id}
            component={component}
            statistic="value"
          />
        </Typography>
      ))}
      <Button
        component={Link}
        to={`/competitions/${competitionId}/matches/${match.match_id}/summary`}
        state={{ from: location }}
        className={classes.matchStatsButton}
      >
        <Typography
          fs="sm"
          fc={{ light: 'gray-0', dark: 'gray-0' }}
          lh="100"
          ta="center"
        >
          Match Stats
        </Typography>
      </Button>
    </div>
  )
}

export default function MatchSummary({
  summary_title,
  teamId,
  competitionId,
  isDesktop,
}: {
  summary_title: string
  teamId: number
  competitionId: number
  isDesktop: boolean
}) {
  const metrics = useMetrics()
  const kpis = metrics.data.filter((m) => m.is_kpi && m.is_used_in_matches)
  const { data: rounds } = useCompetitionRounds({
    competition_id: competitionId,
  })
  const [component, setComponent] = useState('percent')

  const pageSize = isDesktop ? 6 : 3
  const lastRound = useMemo(() => {
    const lastEndedIndex = rounds.findLastIndex((r) => r.status === 'ended')
    return lastEndedIndex === -1 ? 0 : rounds[lastEndedIndex].round_number
  }, [pageSize, rounds])

  const [roundEnd, setRoundEnd] = useState(lastRound)
  const roundStart = roundEnd - pageSize + 1
  const canGoNext = roundEnd < lastRound
  const canGoPrev = roundEnd - pageSize > 0

  const handleNext = () => {
    if (canGoNext) setRoundEnd((prev) => Math.min(prev + pageSize, lastRound))
  }

  const handlePrev = () => {
    if (canGoPrev) setRoundEnd((prev) => Math.max(prev - pageSize, pageSize))
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography fw={500} fc={{ light: 'black', dark: 'gray-3' }} lh="125">
          {summary_title}
        </Typography>
        <div className={classes.headerWrapper}>
          <div className={classes.pagination}>
            <IconChevronLeft
              aria-label="Previous page"
              className={classes.paginationIcon}
              onClick={handlePrev}
              data-pagination-disabled={!canGoPrev}
            />

            <IconChevronRight
              aria-label="Next page"
              className={classes.paginationIcon}
              onClick={handleNext}
              data-pagination-disabled={!canGoNext}
            />
          </div>

          <Select
            data={[
              { value: 'percent', label: 'Target (%)' },
              { value: 'actual', label: 'Actual' },
            ]}
            value={component}
            onChange={(value) => setComponent(value || 'percent')}
            rightSection={<IconChevronDown className={classes.select_icon} />}
            classNames={{
              wrapper: classes.select_wrapper,
              input: classes.select_input,
              dropdown: classes.select_dropdown,
            }}
          />
        </div>
      </div>
      <div className={classes.grid_container}>
        <div className={classes.summary_label_container}>
          <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
            Round
          </Typography>
          <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
            Week
          </Typography>
          <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
            Result
          </Typography>
          <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
            Versus
          </Typography>
          {kpis.map((kpi) => (
            <Typography
              key={kpi.key}
              fc={{ light: 'black', dark: 'gray-3' }}
              lh="150"
            >
              {kpi.short_name || kpi.name}
            </Typography>
          ))}
        </div>
        <Suspense
          fallback={Array.from({ length: pageSize }).map((_, index) => (
            <div key={index} className={classes.summary_column_container}>
              <Skeleton height={16} width="100%" />
              <Skeleton height={16} width="100%" />
              <Skeleton height={32} width="100%" />
              <Skeleton height={64} width={64} circle />
              {kpis.map((kpi) => (
                <Skeleton key={kpi.key} height={16} width="100%" />
              ))}
              <Skeleton height={32} width="100%" />
            </div>
          ))}
        >
          <MatchSummaryInner
            competitionId={competitionId}
            teamId={teamId}
            roundStart={roundStart}
            roundEnd={roundEnd}
            component={component}
            kpis={kpis}
          />
        </Suspense>
      </div>
    </div>
  )
}

interface InnerProps {
  competitionId: number
  teamId: number
  roundStart: number
  roundEnd: number
  component: string
  kpis: Metric[]
}

function MatchSummaryInner(props: InnerProps) {
  const { data: matches } = useMatches({
    competition_id: props.competitionId,
    team_id: props.teamId,
    round_start: props.roundStart,
    round_end: props.roundEnd,
  })

  // No matches in the visible round range (e.g. no ended rounds yet). Bail out
  // before querying metrics: an empty `periods` array serializes to '' and the
  // API rejects it with "Invalid period format".
  if (matches.length === 0) {
    return null
  }

  return <MatchSummaryColumns {...props} matches={matches} />
}

function MatchSummaryColumns(props: InnerProps & { matches: Match[] }) {
  const { matches } = props
  const kpis = props.kpis

  const { data } = useMetricsData({
    period_type: 'round',
    period_range: true,
    periods:
      matches.length > 0
        ? [matches[0].round_start, matches[matches.length - 1].round_start]
        : [],
    subject_type: 'team',
    subject_ids: [props.teamId || 0],
    metrics: kpis.map((kpi) => kpi.key),
    components: kpis.map(() => props.component),
    statistics: kpis.map(() => 'value'),
  })

  const matchesData: MatchStats = useMemo(() => {
    const result: MatchStats = {}

    kpis.forEach((kpi) => {
      result[kpi.key] = {}
    })

    // Map data points to the MatchStats structure
    if (data) {
      data.forEach((dataPoint: DataPoint) => {
        const kpi = kpis.find((k) => k.metric_id === dataPoint.metric)
        if (!kpi) return

        const match = matches.find((m) => m.round_start === dataPoint.period)
        if (!match) return

        result[kpi.key][match.round_number] = {
          value: dataPoint.value,
        }
      })
    }

    return result
  }, [data, kpis, matches])

  return matches.map((match: Match) => (
    <SummaryColumn
      key={match.match_id}
      teamId={props.teamId}
      match={match}
      component={props.component}
      kpis={kpis}
      data={matchesData}
      competitionId={props.competitionId}
    />
  ))
}
